import { useEffect, useRef, useState } from "react";
import analytics from "../services/analytics";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const clampZoom = (value) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const ZoomableImage = ({
  src,
  alt,
  title,
  className = "",
  controlsTourId,
  loading = "eager",
}) => {
  const [zoom, setZoom] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, baseX: 0, baseY: 0 });
  const loadStarted = useRef(performance.now());
  const loadTracked = useRef(false);

  useEffect(() => {
    loadStarted.current = performance.now();
    loadTracked.current = false;
    setZoom(1);
    setTranslate({ x: 0, y: 0 });
    setIsPanning(false);
  }, [src]);

  const changeZoom = (nextZoom) => {
    setZoom(clampZoom(nextZoom));
    setTranslate({ x: 0, y: 0 });
  };

  const handlePointerDown = (event) => {
    if (zoom <= 1) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    panStart.current = {
      x: event.clientX,
      y: event.clientY,
      baseX: translate.x,
      baseY: translate.y,
    };
    setIsPanning(true);
  };

  const handlePointerMove = (event) => {
    if (!isPanning) return;
    setTranslate({
      x: panStart.current.baseX + event.clientX - panStart.current.x,
      y: panStart.current.baseY + event.clientY - panStart.current.y,
    });
  };

  const stopPanning = (event) => {
    if (event?.currentTarget?.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsPanning(false);
  };

  return (
    <div className={`flex min-h-0 flex-col ${className}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {title ? <h2 className="text-xl font-bold text-white">{title}</h2> : <span />}
        <div
          className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-1"
          data-tour={controlsTourId}
        >
          <button
            type="button"
            onClick={() => changeZoom(+(zoom - ZOOM_STEP).toFixed(2))}
            disabled={zoom <= MIN_ZOOM}
            className="h-8 w-8 rounded-full bg-white/15 text-base leading-none text-white hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            type="button"
            onClick={() => changeZoom(1)}
            className="h-8 min-w-12 rounded-full bg-white/15 px-2 text-xs leading-none text-white hover:bg-white/25"
            aria-label="Reset zoom to fit"
            title="Reset to fit"
          >
            Fit
          </button>
          <button
            type="button"
            onClick={() => changeZoom(+(zoom + ZOOM_STEP).toFixed(2))}
            disabled={zoom >= MAX_ZOOM}
            className="h-8 w-8 rounded-full bg-white/15 text-base leading-none text-white hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Zoom in"
          >
            +
          </button>
          <span className="w-11 text-center text-xs tabular-nums text-gray-300" aria-live="polite">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div
          className="aspect-video w-full touch-none overflow-hidden rounded-lg bg-black/60"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopPanning}
          onPointerCancel={stopPanning}
          onPointerLeave={stopPanning}
          style={{ cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default" }}
        >
          <img
            src={src}
            alt={alt}
            loading={loading}
            decoding="async"
            onLoad={() => {
              if (loadTracked.current) return;
              loadTracked.current = true;
              analytics.capture("image_load_completed", {
                source: controlsTourId ? "annotation" : "capture_viewer",
                duration_ms: Math.round(performance.now() - loadStarted.current),
              });
            }}
            draggable={false}
            className={`h-full w-full select-none object-contain object-center ${
              isPanning ? "" : "transition-transform duration-100"
            }`}
            style={{
              transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom})`,
              transformOrigin: "center center",
            }}
            onError={(event) => {
              if (loadTracked.current) return;
              loadTracked.current = true;
              analytics.capture("image_load_failed", {
                source: controlsTourId ? "annotation" : "capture_viewer",
              });
              event.currentTarget.src =
                "https://via.placeholder.com/800x600?text=Image+Not+Available";
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ZoomableImage;
